import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { X } from "@phosphor-icons/react";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import * as selectors from "selectors";
import { useDispatch } from "react-redux";
import actions from "../../actions";
import LoadingButton from "@mui/lab/LoadingButton";
import FilePreview from "./FilePreview";
import { formatFilename } from "utils/snakeCasetoTitleCase";
import { useGlobalToast } from "components/GlobalToastProvider";

export default function UploadDocumentDialog({ open, onClose, onUpload, fileMessage, teams = [] }) {
  if (!open) return null;

  const dispatch = useDispatch();
  const { showToast } = useGlobalToast();
  const documentTypes = useSelector(selectors.getDocumentTypes);
  const [uploading, setUploading] = useState(false);
  const [uploadDocType, setUploadDocType] = useState("");
  const [selectedClientIds, setSelectedClientIds] = useState([]);

  if (documentTypes.length === 0) {
    dispatch(actions.getDocumentTypes());
  }

  const handleClose = () => {
    setUploading(false);
    setUploadDocType("");
    setSelectedClientIds([]);
    onClose();
  };
  const clientIdsInput = teams?.length > 1 ? selectedClientIds : [teams?.[0]?.clientId];

  const handleSubmit = async () => {
    try {
      setUploading(true);
      await onUpload(uploadDocType, clientIdsInput);
      handleClose();
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error uploading document.",
      });
    } finally {
      setUploading(false);
    }
  };

  const disableUploadButton =
    !uploadDocType || (teams?.length > 1 && selectedClientIds.length === 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs">
      <DialogTitle>
        Upload document to client's account
        <IconButton aria-label="close" onClick={handleClose} disabled={uploading}>
          <X />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack gap={"5px"}>
          {teams?.length > 1 && (
            <FormControl sx={{ width: "100%", mb: "8px" }}>
              <InputLabel id="client-select-label">Clients</InputLabel>
              <Select
                multiple
                labelId="client-select-label"
                input={<OutlinedInput label="Clients" />}
                value={selectedClientIds}
                onChange={(event) => setSelectedClientIds(event.target.value)}
                renderValue={(selected) =>
                  selected.length === 0 ? (
                    <Typography color="text.disabled">Select clients</Typography>
                  ) : (
                    <Typography variant="body1">
                      {teams
                        .filter((team) => selected.includes(team.clientId))
                        .map((team) => team.clientName)
                        .join(", ")}
                    </Typography>
                  )
                }
              >
                {teams.map((team) => (
                  <MenuItem key={team.clientId} value={team.clientId}>
                    <Checkbox checked={selectedClientIds.includes(team.clientId)} />
                    <ListItemText primary={team.clientName} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            select
            label="Document Type"
            name="uploadDocType"
            value={uploadDocType}
            onChange={(e) => {
              setUploadDocType(e.target.value);
            }}
            fullWidth
          >
            <MenuItem disabled value={""}>
              Please select
            </MenuItem>
            {documentTypes.map((docType) => {
              return (
                <MenuItem key={docType.id} value={docType.name}>
                  {formatFilename(docType.display_name)}
                </MenuItem>
              );
            })}
          </TextField>
          {fileMessage && <FilePreview message={fileMessage} renderedInModal={true} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <LoadingButton onClick={handleSubmit} loading={uploading} disabled={disableUploadButton}>
          Upload Document
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
